/**
 * ModeToggle — pill toggle in the top nav between the "+ Add Kit" CTA
 * and the profile icon.
 *
 * Two segments: "Hosting" | "Renting". Clicking the inactive segment
 * flips DashboardModeContext AND navigates. Where a parallel view
 * exists in the destination mode the toggle stays in context — e.g.
 * /inbox/orders ↔ /inbox/sales. Otherwise it falls back to the
 * destination mode's Overview (/account/host-dashboard or
 * /account/rentals).
 *
 * Visibility: rendered whenever canSwitchModes === true. Pure renters
 * (canSwitchModes false) never see it.
 */
import React from 'react';
import classNames from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';

import { useDashboardMode } from '../../../../../context/DashboardModeContext';

import css from './ModeToggle.module.css';

const HOSTING_OVERVIEW = '/account/host-dashboard';
const RENTING_OVERVIEW = '/account/rentals';

// Pages with a parallel view in the other mode. Add new pairs here
// when a host-side route gets a renter-side equivalent (or vice versa).
const PARALLEL_PATHS = {
  '/inbox/orders': { hosting: '/inbox/sales' },
  '/inbox/sales': { renting: '/inbox/orders' },
};

const targetForToggle = (currentPath, targetMode) => {
  const parallel = PARALLEL_PATHS[currentPath]?.[targetMode];
  if (parallel) return parallel;
  return targetMode === 'hosting' ? HOSTING_OVERVIEW : RENTING_OVERVIEW;
};

const ModeToggle = ({ className }) => {
  const { currentMode, setMode, canSwitchModes } = useDashboardMode();
  const history = useHistory();
  const location = useLocation();

  if (!canSwitchModes) return null;

  const handleClick = mode => e => {
    e.preventDefault();
    if (mode === currentMode) return;
    setMode(mode);
    history.push(targetForToggle(location.pathname, mode));
  };

  return (
    <div className={classNames(css.root, className)} role="tablist" aria-label="Dashboard mode">
      <button
        type="button"
        role="tab"
        aria-selected={currentMode === 'hosting'}
        className={classNames(css.segment, { [css.segmentActive]: currentMode === 'hosting' })}
        onClick={handleClick('hosting')}
      >
        Hosting
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={currentMode === 'renting'}
        className={classNames(css.segment, { [css.segmentActive]: currentMode === 'renting' })}
        onClick={handleClick('renting')}
      >
        Renting
      </button>
    </div>
  );
};

export default ModeToggle;
